import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Box, Center, Container, Group, Stack, Text, Title } from '@mantine/core';

export interface ErrorPageLayoutProps {
  /** Large visual above the title (e.g. the 404 numerals or a status icon) */
  hero: ReactNode;
  title: string;
  description: ReactNode;
  /** Primary and secondary action buttons */
  actions: ReactNode;
  /** Optional muted line under the actions */
  footer?: ReactNode;
}

/**
 * Full-page layout for 404 and route error screens.
 *
 * Renders outside AppShell and must not depend on AuthContext or TankContext,
 * because the router's errorElement replaces RootLayout (and its providers)
 * when a route throws.
 */
export function ErrorPageLayout({
  hero,
  title,
  description,
  actions,
  footer,
}: ErrorPageLayoutProps): JSX.Element {
  return (
    <Box
      mih="100vh"
      bg="var(--mantine-color-gray-0)"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {/* Header - matches the logged-in AppHeader and PublicToolsLayout bar */}
      <Box h={60} bg="var(--rdh-header-bg)" style={{ flexShrink: 0 }}>
        <Container size="lg" h="100%">
          <Group h="100%">
            <Text
              component={Link}
              to="/"
              size="lg"
              fw={700}
              c="var(--rdh-brand-accent)"
              td="none"
              style={{ letterSpacing: '0.5px' }}
              aria-label="Reef DataHub home"
            >
              Reef DataHub
            </Text>
          </Group>
        </Container>
      </Box>

      {/* Extra bottom padding lifts the block slightly above true centre */}
      <Center px="md" pt="xl" pb={96} style={{ flex: 1 }}>
        <Stack align="center" gap="md" maw={460} ta="center">
          {hero}

          <Title order={1} fz={{ base: 24, sm: 30 }} mt="xs">
            {title}
          </Title>

          <Text c="dimmed" size="sm">
            {description}
          </Text>

          <Group justify="center" gap="sm" mt="sm">
            {actions}
          </Group>

          {footer && (
            <Text size="sm" c="dimmed" mt="md">
              {footer}
            </Text>
          )}
        </Stack>
      </Center>
    </Box>
  );
}
